/**
 * @faylasuf/i18n — message catalogues and language metadata (D-055, D-185,
 * ADR-08 §2.4). Catalogues are data: flat key → ICU MessageFormat string.
 * `ar` and `en` ship built in; further languages arrive as uploaded files in
 * the same format (admin tooling after G2, D-205).
 *
 * The interpreter here is a deliberately small ICU subset with no runtime
 * dependency: `{name}` placeholders and `{name, plural, one{…} other{…}}`
 * (with `#` for the number). Anything else is rendered literally.
 * Missing keys fall back to Arabic and are reported through `reportMissing`.
 */
import ar from "./catalogues/ar.json" with { type: "json" };
import en from "./catalogues/en.json" with { type: "json" };

export type Locale = "ar" | "en";
export type Direction = "rtl" | "ltr";
export type Catalogue = Readonly<Record<string, string>>;

export interface LocaleMeta {
  readonly locale: Locale;
  readonly direction: Direction;
  /** Matches `--font-sans` of the old `index.css` (self-hosted Cairo, D-146). */
  readonly fontStack: string;
  /** Digit style: the old app renders Western digits everywhere (`.numeric-en`, JetBrains Mono). */
  readonly digits: "latn";
  /** Date format of the header date line (App.tsx 25776: en-US MM-DD-YYYY). */
  readonly dateFormat: string;
  readonly nativeName: string;
}

export const LOCALES: Readonly<Record<Locale, LocaleMeta>> = {
  ar: { locale: "ar", direction: "rtl", fontStack: '"Cairo", "Inter", "Segoe UI", "Tahoma", "Arial", ui-sans-serif, system-ui, sans-serif', digits: "latn", dateFormat: "MM-DD-YYYY", nativeName: "العربية" },
  en: { locale: "en", direction: "ltr", fontStack: '"Cairo", "Inter", "Segoe UI", "Tahoma", "Arial", ui-sans-serif, system-ui, sans-serif', digits: "latn", dateFormat: "MM-DD-YYYY", nativeName: "English" },
};

export const FALLBACK_LOCALE: Locale = "ar";

export const CATALOGUES: Readonly<Record<Locale, Catalogue>> = { ar, en };

export type Params = Readonly<Record<string, string | number>>;
export type Translate = (key: string, params?: Params) => string;

export interface CreateTOptions {
  /** Called once per missing key per `t` instance (ADR-08 §2.4: reported to the admin console). */
  readonly reportMissing?: (key: string, locale: Locale) => void;
  /** Extra catalogue layered over the built-in one (uploaded languages, tests). */
  readonly overlay?: Catalogue;
}

/** Splits `a{b{c}}d` at the top-level commas/braces of one ICU argument. */
function splitArg(body: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of body) {
    if (ch === "{") depth++;
    if (ch === "}") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/** Parses `one{…} other{…}` into a map; brace-balanced. */
function parseOptions(src: string): Record<string, string> {
  const opts: Record<string, string> = {};
  let i = 0;
  while (i < src.length) {
    const open = src.indexOf("{", i);
    if (open < 0) break;
    const name = src.slice(i, open).trim();
    let depth = 1;
    let j = open + 1;
    while (j < src.length && depth > 0) {
      if (src[j] === "{") depth++;
      else if (src[j] === "}") depth--;
      j++;
    }
    opts[name] = src.slice(open + 1, j - 1);
    i = j;
  }
  return opts;
}

export function formatMessage(message: string, params: Params | undefined, locale: Locale): string {
  let out = "";
  let i = 0;
  while (i < message.length) {
    const open = message.indexOf("{", i);
    if (open < 0) {
      out += message.slice(i);
      break;
    }
    out += message.slice(i, open);
    let depth = 1;
    let j = open + 1;
    while (j < message.length && depth > 0) {
      if (message[j] === "{") depth++;
      else if (message[j] === "}") depth--;
      j++;
    }
    const body = message.slice(open + 1, j - 1);
    const [name = "", kind, rest] = splitArg(body);
    const value = params?.[name];
    if (kind === "plural" && rest !== undefined) {
      const n = typeof value === "number" ? value : Number(value ?? 0);
      const opts = parseOptions(rest);
      const exact = opts[`=${n}`];
      const category = new Intl.PluralRules(locale).select(n);
      const chosen = exact ?? opts[category] ?? opts["other"] ?? "";
      out += formatMessage(chosen.replace(/#/g, String(n)), params, locale);
    } else if (value !== undefined) {
      out += String(value);
    } else {
      out += `{${name}}`;
    }
    i = j;
  }
  return out;
}

export function createT(locale: Locale, options: CreateTOptions = {}): Translate {
  const primary: Catalogue = options.overlay ? { ...CATALOGUES[locale], ...options.overlay } : CATALOGUES[locale];
  const fallback = CATALOGUES[FALLBACK_LOCALE];
  const reported = new Set<string>();
  return (key, params) => {
    let msg = primary[key];
    if (msg === undefined) {
      if (!reported.has(key)) {
        reported.add(key);
        options.reportMissing?.(key, locale);
      }
      msg = fallback[key];
      if (msg === undefined) return key;
    }
    return formatMessage(msg, params, locale);
  };
}

/** Placeholder names of one message, for catalogue validation (ADR-08 §2.4). */
export function placeholdersOf(message: string): string[] {
  const names = new Set<string>();
  const re = /\{\s*([A-Za-z0-9_]+)\s*[,}]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(message)) !== null) if (m[1] !== undefined) names.add(m[1]);
  return [...names].sort();
}

/** Translates an API error `{code, params}` (ADR-06 §2.3); unknown codes use `error.UNKNOWN`. */
export function translateError(t: Translate, code: string, params?: Params): string {
  const key = `error.${code}`;
  const msg = t(key, params);
  return msg === key ? t("error.UNKNOWN", { code }) : msg;
}
